import React, { useCallback, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import DataSource from "devextreme/data/data_source";
import data from "../../../../assets/apiRoutes.json";
import { GridLayout } from "../../../../common/GridLayout";
import GridLayoutManager from "../../../../gridLayoutCustomization/GridLayoutManager";
import { GRID_PAGE_SIZES, CURRENCY_FORMAT, DEFAULT_LAYOUT_NAME } from "../constants";
import {
    Column,
    DataGrid,
    GroupPanel,
    Grouping,
    FilterRow,
    FilterPanel,
    SearchPanel,
    Sorting,
    Paging,
    Selection,
    Toolbar,
    Item,
    Export,
    Pager,
    ColumnChooser,
    ColumnChooserSearch,
    StateStoring,
} from "devextreme-react/data-grid";

/**
 * Cash receipts for one customer, backed by ar/CashReceipt/GetCustomerCashReceipts -
 * a purpose-built endpoint/DTO (CustomerCashReceiptDTO) rather than the shared
 * CashReceiptHeaderDTO used by Insert/ApplyCustomerPayment. Mirrors
 * grdBottomCashReceipts in OMD0010. Amount is the sum of the receipt's payment
 * rows, computed server-side.
 */
const CashReceiptsTab = (props) => {
    const { customerId, gridKey } = props;
    const accessToken = props.oidc.user.access_token;
    const dataGrid = useRef(null);

    // Named grid layouts: GridLayoutManager switches currentLayout, and the state
    // storage requests carry it so each layout keeps its own saved state.
    const [currentLayout, setCurrentLayout] = useState(DEFAULT_LAYOUT_NAME);
    const loadState = useCallback(() => GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout), [gridKey, currentLayout]);
    const saveState = useCallback((state) => GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout), [gridKey, currentLayout]);

    const dataSource = useMemo(() => {
        if (!customerId) {
            return [];
        }
        const store = createStore({
            key: 'RowId',
            loadParams: { CustomerRowId: customerId },
            loadUrl: `${window._env_.REACT_APP_APIS}/${data.AR.GetCustomerCashReceipts}`,
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        });
        const source = new DataSource({ store: store });
        // Matches qryARCashReceiptHeaderBottom: ORDER BY
        // ARCASHRECEIPTSHEADER.Status, ARCASHRECEIPTSHEADER.DateReceived
        source.sort([
            { selector: 'StatusFlag', desc: false },
            { selector: 'DateReceived', desc: false },
        ]);
        return source;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    return (
        <DataGrid
            onExporting={(e) => {
                if (window.handleDevExtremeDataGridExport) {
                    window.handleDevExtremeDataGridExport(e);
                }
            }}
            ref={dataGrid}
            className="tabbed-detail-grid"
            dataSource={dataSource}
            keyExpr="RowId"
            allowColumnReordering={true}
            allowColumnResizing={true}
            showBorders={true}
            hoverStateEnabled={true}
            columnAutoWidth={true}
            remoteOperations={true}
        >
            <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: 'Search column' }} />
            </ColumnChooser>
            <Export enabled={true} allowExportSelectedData={true} />
            <Selection mode="single" />
            <Sorting visible={true} mode="multiple" />
            <Paging defaultPageSize={10} />
            <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={GRID_PAGE_SIZES} showNavigationButtons={true} />
            <FilterRow visible={true} />
            <FilterPanel visible={true} />
            <SearchPanel visible={true} width={240} placeholder="Search..." />
            <GroupPanel visible={true} />
            <Grouping autoExpandAll={false} />
            <StateStoring enabled={true} type="custom" customLoad={loadState} storageKey={gridKey} customSave={saveState} />

            {/* Mirrors grdBottomCashReceipts */}
            <Column dataField="RecordNumber" caption="Record #" />
            <Column dataField="DateReceived" caption="Date Received" dataType="date" />
            <Column dataField="Description" caption="Description" />
            <Column dataField="StatusDescription" caption="Status" />
            <Column dataField="FiscalPeriod" caption="Fiscal Period" dataType="number" />
            <Column dataField="FiscalYear" caption="Fiscal Year" dataType="number" />
            <Column dataField="Amount" caption="Amount" dataType="number" format={CURRENCY_FORMAT} />
            <Column dataField="CustomerCode" caption="Customer" />
            <Column dataField="OpenCreditTransactionNumber" caption="Open Credit Transaction #" />
            <Column dataField="OpenCreditReferenceNumber" caption="Open Credit Reference #" />

            {/* Column chooser (hidden by default) */}
            <Column dataField="CustomerName" caption="Customer Name" visible={false} />
            <Column dataField="Reference" caption="Reference" visible={false} />
            <Column dataField="CheckNumber" caption="Check #" visible={false} />
            <Column dataField="ProfileCode" caption="Profile" visible={false} />
            <Column dataField="ControlAmount" caption="Control Amount" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="OpenCreditAmount" caption="Open Credit Amount" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="OpenCreditDescription" caption="Open Credit Description" visible={false} />
            <Column dataField="TenderedAmount" caption="Tendered Amount" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="CommitDate" caption="Commit Date" dataType="date" visible={false} />
            <Column dataField="Comment" caption="Comment" visible={false} />
            <Column dataField="CreationUserId" caption="Created By" visible={false} />
            <Column dataField="CreationDateTime" caption="Creation Date" dataType="datetime" visible={false} />
            <Column dataField="LastUpdateUserId" caption="Last Updated By" visible={false} />
            <Column dataField="LastUpdateDateTime" caption="Last Updated" dataType="datetime" visible={false} />
            <Column dataField="CompanyId" caption="Company ID" visible={false} />
            <Column dataField="KeySequence" caption="Key Sequence" dataType="number" visible={false} />
            <Column dataField="Module" caption="Module" visible={false} />
            <Column dataField="ReceiptMethodType" caption="Receipt Method Type" visible={false} />

            {/* Technical columns stay hidden and out of the chooser */}
            <Column dataField="RowId" caption="Row Id" visible={false} showInColumnChooser={false} />
            <Column dataField="RCustomer" caption="Customer Id" visible={false} showInColumnChooser={false} />
            <Column dataField="RecordType" caption="Record Type" visible={false} showInColumnChooser={false} />
            <Column dataField="StatusFlag" caption="Status Flag" visible={false} showInColumnChooser={false} />
            <Toolbar>
                <Item location="after">
                    <GridLayoutManager gridKey={gridKey} dataGrid={dataGrid} setCurrentLayout={setCurrentLayout} />
                </Item>
                <Item name="exportButton" />
                <Item name="columnChooserButton" />
            </Toolbar>
        </DataGrid>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(CashReceiptsTab);
